<template>
  <div
    :id="listId"
    ref="scrollRoot"
    class="input-list"
    :class="{ 'input-list--open': listActive }"
    role="listbox"
    :aria-labelledby="labelledBy"
    :aria-multiselectable="multiple ? 'true' : 'false'"
    :aria-hidden="listActive ? 'false' : 'true'"
  >
    <ul role="presentation">
      <li
        v-for="(item, inx) in items"
        v-show="isSearched(item)"
        :key="inx"
        :class="[
          'input-list--item',
          isSelected(item) ? 'input-list--item-selected' : '',
          isDisabled(item) ? 'input-list--item-disabled' : '',
          'accessibility-outline',
          'outline-inset',
        ]"
        role="option"
        :aria-selected="isSelected(item) ? 'true' : 'false'"
        :aria-disabled="isDisabled(item) ? 'true' : undefined"
        tabindex="0"
        :tabindex="!listActive || isDisabled(item) ? -1 : 0"
        :aria-label="item.label"
        @click="selectItem(item)"
        @keydown.enter.prevent="selectItem(item)"
        @keydown.space.prevent="selectItem(item)"
      >
        <span
          v-if="iconLeft || itemsContainIcon"
          class="input-list--item-icon"
          aria-hidden="true"
        >
          <span><i
            :class="item.icon"
            :style="{ color: item.iconColor }"
          /></span>
        </span>
        <span
          v-else-if="itemsContainImage"
          :class="itemsContainImage ? 'input-list--item-image' : ''"
          aria-hidden="true"
        >
          <span :style="{ 'background-image': `url(${item.url})` }" />
        </span>
        <span
          v-if="iconLeft || itemsContainIcon || itemsContainImage"
          class="input-list--item-divider"
          :style="visibilityItemIcon(item)"
          aria-hidden="true"
        />
        <span class="input-list--item-label">{{ item.label }}</span>
      </li>
      <li
        class="input-list--item input-list--item-empty"
        role="presentation"
        aria-hidden="true"
      >
        <span class="input-list--item-label">{{ emptyText }}</span>
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  name: 'BaseItemList',
  model: {
    prop: 'value',
    event: 'select',
  },
  props: {
    value: {
      type: [Array, String, Number, Object],
      default: () => ([]),
    },
    listActive: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array,
      required: true,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    iconLeft: {
      type: String,
      default: '',
    },
    listId: {
      type: String,
      default: '',
    },
    labelledBy: {
      type: String,
      default: '',
    },
    emptyText: {
      type: String,
      default: 'Keine Auswahlmöglichkeiten vorhanden.',
    },
    rootClass: {
      type: String,
      default: '',
    },
    search: {
      type: String,
      default: undefined,
    },
    caseSensitive: Boolean,
  },
  data() {
    return {
      selectedListWidth: 0,
      itemsContainIcon: false,
      itemsContainImage: false,
    };
  },
  computed: {
    displayedItems() {
      if (!this.items || this.items.length === 0) {
        return [{ label: this.emptyText, disabled: true }];
      }
      return this.items.filter((x) => x.label);
    },
  },
  watch: {
    items(val) {
      if (val.length === 0) {
        val.push({ label: this.emptyText, disabled: true });
      }
      if (val.filter((x) => !x.label).length) {
        console.warn('<BaseItemList>: Items must have a label property.', val.filter((x) => !x.label));
      }
      this.setItemsContainImageOrIcon();
    },
  },
  beforeMount() {
    this.setItemsContainImageOrIcon();
  },
  mounted() {
    if (this.rootClass) {
      const rootTarget = document.querySelector(this.rootClass);
      if (rootTarget) {
        rootTarget.appendChild(this.$refs.scrollRoot);
      }
    }
  },
  methods: {
    isSearched(item) {
      if (item === undefined) return false;
      if (`${this.search || ''}`.trim() === '') return true;

      if (this.caseSensitive) {
        return item.label.includes(this.search);
      }

      return item.label.toLowerCase().includes(this.search.toLowerCase());
    },
    setItemsContainImageOrIcon() {
      let hasIcon = false;
      let hasImage = false;
      for (let i = 0; i < this.displayedItems.length; i += 1) {
        hasImage = hasImage || this.displayedItems[i].url;
        hasIcon = hasIcon || this.displayedItems[i].icon;

        if (hasIcon || hasImage) break;
      }

      this.itemsContainImage = hasImage;
      this.itemsContainIcon = hasIcon;
    },
    visibilityItemIcon(item) {
      return {
        visibility: item.divider !== false,
      };
    },
    isSelected(item) {
      if (this.multiple) {
        return this.value === null ? false : this.value.includes(item.value);
      }
      return item.value === this.value;
    },
    isDisabled(item) {
      return item.disabled;
    },
    selectItem(item) {
      if (this.value instanceof Array && this.multiple) {
        let newValue;
        if (this.value.includes(item.value)) {
          newValue = this.value.filter((x) => x !== item.value);
        } else {
          newValue = [...this.value, item.value];
        }
        this.$emit('select', newValue);

        // Create consistent payload for multiple selection
        const selectedItems = this.displayedItems
          .filter((x) => newValue.includes(x.value))
          .map((i) => Object.entries(i).reduce((acc, [key, value]) => {
            if (key !== 'value' && key !== 'label') {
              acc[key] = value;
            }
            return acc;
          }, {}));
        this.$emit('payload', selectedItems);
      } else {
        this.$emit('select', item.value);
        this.$emit('payload', Object.entries(this.displayedItems.find((x) => x.value === item.value)).reduce((acc, [key, value]) => {
          if (key !== 'value' && key !== 'label') {
            // eslint-disable-next-line no-param-reassign
            acc[key] = value;
          }
          return acc;
        }, {}));
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@mixin icons {
    min-width: 2.5em;
    font-size: 1.1em;
    padding: 0 0.8em;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-basis: 2em;
}

@mixin divider {
    border-left: 1px solid var(--base-input--accent-color);
    height: var(--base-input--divider-height);
}

.input-list {
    position: relative;
    height: auto;
    max-height: 13.375rem;
    width: 100%;
    overflow: hidden;
    cursor: pointer;
    background-color: #fff;
    border: 0 solid transparent;
    box-shadow: 0 0 0 0 transparent;
    transition: all calc(var(--base-select--open-transition) / 2) ease-in-out, height var(--base-select--open-transition) ease-in-out;
    display: none;

    .input-list--item-empty {
      display: block;
      cursor: default;
      pointer-events: none;
    }

    ul .input-list--item:not(.input-list--item-empty):not([style*="display: none"]) ~ .input-list--item-empty {
      display: none;
    }

    .input-list--item {
        display: flex;
        padding: calc((var(--base-input--padding-top) + var(--base-input--padding-bottom)) / 2) 0;
        line-height: 1.25;
        min-height: calc(var(--base-input--padding-top) + var(--base-input--padding-bottom) + var(--base-input--input-height));
        align-items: center;
        font-size: 1em;
        font-weight: inherit;
        background-color: transparent;
        transition: background-color var(--base-select--item-transition) ease-in-out;
        pointer-events: none;

        .input-list--item-label {
            opacity: .25;
        }

        &:not(.input-list--item-disabled) {
            pointer-events: all;

            .input-list--item-label {
                opacity: 1;
            }

            &:hover {
                background-color: var(--base-select--item-hover-color);
            }
        }
    }

    &--item-icon {
        @include icons;
    }

    &--item-image {
        @include icons;
        padding: 0 1.5em;
        height: var(--base-input--input-height);
        min-width: 5.5em;

        span {
            height: 100%;
            width: 100%;
            background-size: cover;
        }
    }

    &--item-divider {
        @include divider;
    }

    &--item-label {
        display: flex;
        align-items: center;
        text-align: left;
        flex: 1 0 0;
        padding: 0 1.5em;
    }

    &--open {
        display: block;
        overflow-y: scroll;

        .input-list--item-selected {
            background-color: var(--base-select--item-selected-color);
        }
    }
}
</style>
